import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useDisclosure } from '@chakra-ui/react';

import { AccountClosureReason } from '__generated__/GQL';
import FormField from 'components/_core/form/Field';
import Input from 'components/_core/form/Input';
import SunshineCard from 'components/_core/SunshineCard';
import { logEvent } from 'features/Analytics/analytics';

import reasonLocales from '../../locales';
import PricingIssueModal from '../FormStepSatisfaction/components/PricingIssueModal';

type Option = {
  reason: AccountClosureReason;
  title: string;
  explanationFieldName?: string;
};

type FormCheckboxGroupProps = {
  fieldName: string;
  options: Option[];
};

const FormCheckboxGroup: FC<FormCheckboxGroupProps> = ({
  fieldName,
  options,
}) => {
  const { control, register, setValue } = useFormContext();
  const { onOpen, ...modalProps } = useDisclosure();

  return (
    <Controller
      control={control}
      name={fieldName}
      render={({ field: { onChange, value } }) => (
        <>
          <SunshineCard.Group>
            {options.map((option) => {
              const isChecked = value.has(option.reason);

              return (
                <SunshineCard
                  id={option.reason}
                  isChecked={isChecked}
                  key={option.reason}
                  onChange={() => {
                    if (isChecked) {
                      value.delete(option.reason);

                      if (option.explanationFieldName) {
                        setValue(option.explanationFieldName, '');
                      }
                    } else {
                      if (
                        [
                          AccountClosureReason.SatisfiedPricingIssue,
                          AccountClosureReason.UnsatisfiedPricingIssue,
                        ].includes(option.reason)
                      ) {
                        onOpen();
                        logEvent({
                          name: `Account Closure reason ${option.reason} selected`,
                        });
                      }
                      value.add(option.reason);
                    }

                    onChange(value);
                  }}
                  title={option.title}
                  variant="checkbox"
                >
                  {isChecked && option.explanationFieldName ? (
                    <FormField
                      label={reasonLocales.tellUsMoreInputLabel}
                      marginTop="space-12"
                    >
                      <Input
                        placeholder={reasonLocales.tellUsMoreInputPlaceholder}
                        {...register(option.explanationFieldName)}
                      />
                    </FormField>
                  ) : null}
                </SunshineCard>
              );
            })}
          </SunshineCard.Group>
          <PricingIssueModal {...modalProps} />
        </>
      )}
    />
  );
};

export default FormCheckboxGroup;
